import { Section, SectionParagraph, SectionTitle } from '@/components/shared/Content';
import { Flex, ResponsiveBreak, font, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import type React from 'react';
import SlickSlider, { type Settings } from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import styled from 'styled-components';
import { CheckMarkCircleIcon, PetLandingPageType } from './shared';

const PricingWrapper = styled(Section)`
  width: 100%;
`;

const Heading = styled(SectionTitle)`
  margin-bottom: 24px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 16px;
    `}
`;

const SubHeading = styled(SectionParagraph)`
  margin-bottom: 24px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 48px;
    `}
`;

const Slider = styled(SlickSlider)`
  width: 100%;
  display: block;

  ${MEDIA_QUERIES.tabletAndDesktop`
        display: none;
    `}

  .slick-track {
    padding: 52px 0;
  }

  .slick-dots {
    display: flex;
    justify-content: center;

    li {
      margin: 0 0.2rem;

      button {
        &:before {
          opacity: 1;
          color: ${themedColor('glass')};
          font-size: 1rem;
        }
      }

      &.slick-active {
        button {
          &:before {
            color: ${themedColor('primary')};
          }
        }
      }
    }
  }

  * {
    outline: none;
  }
`;

const PackagesWrapper = styled(Flex).attrs({ justifyContent: 'space-between' })`
  display: none;
  width: 100%;

  ${MEDIA_QUERIES.tabletAndDesktop`
        display: flex;
        max-width: 1140px;
        gap: 18px;
    `}
`;

const PackageHeading = styled(Flex).attrs({
  flexDirection: 'column',
  alignItems: 'center',
})`
  ${font('main', { fontSize: '1.8rem', lineHeight: '1.5' })};
  margin-bottom: 24px;

  > span {
    text-align: center;
  }

  > span:last-child {
    font-size: 1.6rem;
  }
`;

const PackageItems = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
`;

const PackageListItem = styled(Flex).attrs({ as: 'li', alignItems: 'center' })`
  ${font('main', { fontSize: '1.4rem', lineHeight: '1.5' })};
  gap: 8px;
  margin-bottom: 12px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 14px;
    `}
`;

const CardWrapperForSlider = styled(Flex)`
  padding: 0 8px;
  width: 340px;
  height: 420px;
`;

const PackageCard = styled(Flex).attrs({ flexDirection: 'column' })`
  background-color: ${themedColor('white')};
  padding: 24px 24px 12px 24px;
  box-shadow: 0px 12px 24px rgba(0, 0, 0, 0.06);
  width: 100%;
  height: 100%;
  border-radius: 12px;
  flex-grow: 1;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin: 0;
        width: 340px;
        height: auto;
        padding: 24px 50px;
        box-shadow: 0px 15px 54px rgba(0, 0, 0, 0.06);
    `}
`;

const basicPackageItems = ['Wellness Exam', 'Vaccines', 'Wellness Testing', 'Medical Advice Chat'];

const packages = [
  {
    name: {
      [PetLandingPageType.Dog]: 'Preventative',
      [PetLandingPageType.Cat]: 'Preventative',
      [PetLandingPageType.PetInsurance]: 'Preventative for Adult Pets',
    },
    price: {
      [PetLandingPageType.Dog]: '15',
      [PetLandingPageType.Cat]: '10',
      [PetLandingPageType.PetInsurance]: '10',
    },
    includedItems: basicPackageItems,
  },
  {
    name: {
      [PetLandingPageType.Dog]: 'Preventative+ for Adult Dogs',
      [PetLandingPageType.Cat]: 'Preventative+ for Adult Cats',
      [PetLandingPageType.PetInsurance]: 'Preventative+ for Adult Pets',
    },
    price: {
      [PetLandingPageType.Dog]: '22',
      [PetLandingPageType.Cat]: '15',
      [PetLandingPageType.PetInsurance]: '15',
    },
    includedItems: [...basicPackageItems, 'Flea/Tick or Heartworm Medication', 'Routine Dental Cleaning'],
  },
  {
    name: {
      [PetLandingPageType.Dog]: 'Preventative for Puppies',
      [PetLandingPageType.Cat]: 'Preventative for Kittens',
      [PetLandingPageType.PetInsurance]: 'Preventative for Puppies/Kittens',
    },
    price: {
      [PetLandingPageType.Dog]: '40',
      [PetLandingPageType.Cat]: '31',
      [PetLandingPageType.PetInsurance]: '31',
    },
    includedItems: [...basicPackageItems, 'Spay/Neuter', 'Microchip', 'Flea/Tick or Heartworm Medication'],
  },
];

interface PricingProps {
  petLandingPageType: PetLandingPageType;
}

const sliderSettings: Settings = {
  dots: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  infinite: false,
  pauseOnHover: true,
  swipeToSlide: true,
  centerMode: true,
  centerPadding: '10px',
};

const Pricing: React.FC<PricingProps> = ({ petLandingPageType }) => {
  return (
    <PricingWrapper justifyContent="center" py={[40, 70]} pb={[40, 90]}>
      <Flex flexDirection="column" alignItems="center" px={[16, 16, 0]}>
        <Heading>
          {petLandingPageType === PetLandingPageType.Cat ? 'Start Saving on' : 'Huge Savings on'}
          <ResponsiveBreak display={['block', 'block', 'inline']} />
          {petLandingPageType === PetLandingPageType.Cat ? 'Tests, Vaccines, &' : 'Tests, Vaccines, and'}
          <ResponsiveBreak display={['block', 'block', 'inline']} />
          Routine Care
        </Heading>
        <SubHeading>
          <>
            Preventative packages cover
            <ResponsiveBreak display={['block', 'inline']} />
            vet care you’re probably
            <ResponsiveBreak display={['block', 'inline']} />
            already paying for
          </>
        </SubHeading>
      </Flex>
      <PackagesWrapper>
        {packages.map(({ name, price, includedItems }) => (
          <PackageCard key={name[petLandingPageType]}>
            <PackageHeading>
              <span style={{ fontWeight: 700 }}>{name[petLandingPageType]}</span>
              <span>{`From +$${price[petLandingPageType]}/Mo`}</span>
            </PackageHeading>
            <PackageItems>
              {includedItems.map(item => (
                <PackageListItem key={item}>
                  <CheckMarkCircleIcon />
                  {item}
                </PackageListItem>
              ))}
            </PackageItems>
          </PackageCard>
        ))}
      </PackagesWrapper>
      <Slider {...sliderSettings}>
        {packages.map(({ name, price, includedItems }) => (
          <CardWrapperForSlider key={name[petLandingPageType]}>
            <PackageCard>
              <PackageHeading>
                <span style={{ fontWeight: 700 }}>{name[petLandingPageType]}</span>
                <span>{`from +$${price[petLandingPageType]}/Mo`}</span>
              </PackageHeading>
              <PackageItems>
                {includedItems.map(item => (
                  <PackageListItem key={item}>
                    <CheckMarkCircleIcon />
                    {item}
                  </PackageListItem>
                ))}
              </PackageItems>
            </PackageCard>
          </CardWrapperForSlider>
        ))}
      </Slider>
    </PricingWrapper>
  );
};

export default Pricing;
